"use client";

import { useEffect, useRef } from "react";
import { useAuthStore } from "@/modules/auth/stores/useAuthStore";
import { useActiveStore } from "@/modules/store/stores/useActiveStore";
import { useCartStore } from "@/modules/cart";
import { useFavoritesStore } from "@/modules/favorites";
import { fetchSessionBootstrap, isAnonymousKey, sessionKey } from "../services/session-bootstrap";

/**
 * Carrega o estado pessoal (carrinho + favoritos) numa chamada, assim que
 * sessão e loja estão resolvidas.
 *
 * O ponto que faz isso valer: os dois stores ADOTAM a carga de forma SÍNCRONA,
 * antes da resposta chegar. Sem isso o `useCartSync` e a hidratação ociosa
 * disparariam suas próprias buscas na janela em que o bootstrap está em voo — e
 * ele seria uma chamada A MAIS em vez de duas a menos.
 *
 * Falha não quebra nada: os dois caminhos antigos são liberados e assumem.
 */
export function useSessionBootstrap(): void {
    const authStatus = useAuthStore((state) => state.status);
    const storeReady = useActiveStore((state) => state.ready);
    const storeId = useActiveStore((state) => state.storeId);

    // Uma vez por (cliente, loja): trocar de loja recarrega o carrinho.
    const loadedKey = useRef<string | null>(null);

    useEffect(() => {
        if (authStatus === "loading" || !storeReady || storeId <= 0) return;

        const cpfCnpj = useAuthStore.getState().user?.cpfCnpj;
        const key = sessionKey(cpfCnpj, storeId);
        if (loadedKey.current === key) return;
        // Saiu da conta (logout ou sessão expirada): o carrinho e os favoritos
        // em memória são de quem estava logado. A sessão anônima só enxerga as
        // linhas sem dono, então nada do que estava na tela vale — limpa já,
        // em vez de deixar o carrinho do cliente visível até a resposta chegar.
        const loggedOut = loadedKey.current !== null && !isAnonymousKey(loadedKey.current) && !cpfCnpj;
        loadedKey.current = key;

        const request = fetchSessionBootstrap(storeId);

        // Adoção SÍNCRONA: a partir daqui nem o `useCartSync` nem a hidratação
        // ociosa buscam nada — eles enxergam a carga como já assumida.
        useCartStore.getState().adopt(key, loggedOut ? null : useCartStore.getState().cart);
        if (loggedOut) useFavoritesStore.getState().reset();
        if (cpfCnpj) {
            void useFavoritesStore
                .getState()
                .adopt(
                    cpfCnpj,
                    // REJEITA quando o bootstrap falha: resolver com [] marcaria a
                    // lista como carregada e o coração ficaria vazio para sempre.
                    request.then((data) => {
                        if (!data) throw new Error("session bootstrap failed");
                        return data.favorites;
                    })
                );
        }

        void request.then((data) => {
            if (!data) {
                // Libera os caminhos de recuperação.
                loadedKey.current = null;
                useCartStore.setState({ loadedKey: null });
                return;
            }
            useCartStore.getState().adopt(key, data.cart);
        });
    }, [authStatus, storeReady, storeId]);
}
